import React, { useRef } from "react";
import { StyleSheet, View, Text, Pressable, TextInput, Image } from "react-native";
import { ModalCloseLink } from "../Screens/PaymentProcessing/GradFundSetupInfo";
import { SPOTLIGHT_TARGETS, spotlightTarget } from "./SpotlightTutorial/spotlightTargets";

const PHOTO_MESSAGE_COLORS = {
    info: "#1565c0",
    success: "#2e7d32",
    error: "#c62828",
};

const LabeledInput = ({ label, ...inputProps }) => (
    <View style={styles.field}>
        <Text style={styles.label}>{label}</Text>
        <TextInput style={styles.input} placeholderTextColor="#9aa0a6" {...inputProps} />
    </View>
);

const GradFundGoalEditor = ({
    title,
    description,
    targetAmount,
    onChangeField,
    onSave,
    onCancel,
    onResetTarget,
    canResetTarget = false,
    descriptionMaxLength,
    amountRaised,
    photoLabel = "Upload Photo",
    onPhotoSelected,
    onRequestPhoto,
    photoPreviewUri,
    photoMessage,
}) => {
    const fileInputRef = useRef(null);
    const openPhotoPicker = () => fileInputRef.current?.click();
    const descriptionLength = description?.length ?? 0;

    return (
        <View style={styles.card} {...spotlightTarget(SPOTLIGHT_TARGETS.GOAL_EDITOR)}>
            <Text style={styles.heading}>Set Your Goal</Text>
            <Text style={styles.subheading}>Tell friends and family what your Graduation Fund is for.</Text>

            <LabeledInput
                label="Title"
                placeholder="Graduation Fund Title"
                value={title ?? ""}
                onChangeText={(text) => onChangeField("title", text)}
            />
            <View style={styles.field}>
                <Text style={styles.label}>Description</Text>
                <TextInput
                    style={[styles.input, styles.multilineInput]}
                    placeholder="Graduation Fund Description"
                    placeholderTextColor="#9aa0a6"
                    value={description ?? ""}
                    onChangeText={(text) => onChangeField("description", descriptionMaxLength ? text.slice(0, descriptionMaxLength) : text)}
                    multiline
                    numberOfLines={3}
                    maxLength={descriptionMaxLength}
                />
                {descriptionMaxLength ? (
                    <Text style={styles.counter}>{descriptionLength}/{descriptionMaxLength}</Text>
                ) : null}
            </View>
            <LabeledInput
                label="Target Amount ($)"
                placeholder="e.g. 5000"
                keyboardType="numeric"
                value={targetAmount ?? ""}
                onChangeText={(text) => onChangeField("targetAmount", text)}
            />
            {amountRaised !== undefined && <Text style={styles.amountRaised}>Amount raised so far: ${amountRaised}</Text>}

            <Pressable style={styles.photoButton} onPress={() => onRequestPhoto(openPhotoPicker)} accessibilityRole="button">
                <Text style={styles.photoButtonText}>{photoLabel}</Text>
            </Pressable>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={onPhotoSelected} style={{ display: "none" }} />
            {photoMessage?.text ? (
                <Text style={[styles.photoMessage, { color: PHOTO_MESSAGE_COLORS[photoMessage.type] }]}>{photoMessage.text}</Text>
            ) : null}
            {photoPreviewUri ? <Image source={{ uri: photoPreviewUri }} style={styles.photoPreview} /> : null}

            <Pressable style={styles.saveButton} onPress={onSave} accessibilityRole="button">
                <Text style={styles.saveButtonText}>Save</Text>
            </Pressable>
            {canResetTarget && <ModalCloseLink label="Reset Grad Fund Target Amount" onPress={onResetTarget} />}
            <ModalCloseLink label="Cancel" onPress={onCancel} />
        </View>
    );
};

const styles = StyleSheet.create({
    card: {
        width: "100%",
        maxWidth: 440,
        padding: 24,
        backgroundColor: "#fefde2",
        borderRadius: 15,
        alignItems: "center",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
    },
    heading: {
        fontSize: 24,
        fontWeight: "bold",
        color: "#2f5138",
        marginBottom: 4,
        textAlign: "center",
    },
    subheading: {
        fontSize: 14,
        color: "#666",
        textAlign: "center",
        marginBottom: 16,
    },
    field: {
        width: "100%",
        marginBottom: 12,
    },
    label: {
        fontSize: 13,
        fontWeight: "600",
        color: "#2f5138",
        marginBottom: 4,
    },
    input: {
        width: "100%",
        padding: 10,
        fontSize: 16,
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 10,
        backgroundColor: "#fff",
    },
    multilineInput: {
        minHeight: 72,
        textAlignVertical: "top",
    },
    counter: {
        fontSize: 11,
        color: "#94a3b8",
        textAlign: "right",
        marginTop: 2,
    },
    amountRaised: {
        fontSize: 13,
        color: "#666",
        marginBottom: 12,
    },
    photoButton: {
        paddingVertical: 8,
        paddingHorizontal: 18,
        borderRadius: 20,
        borderWidth: 1,
        borderColor: "#4c956c",
        marginBottom: 8,
    },
    photoButtonText: {
        fontSize: 14,
        fontWeight: "600",
        color: "#4c956c",
    },
    photoMessage: {
        fontSize: 13,
        textAlign: "center",
        marginBottom: 8,
    },
    photoPreview: {
        width: 80,
        height: 80,
        borderRadius: 10,
        marginBottom: 8,
    },
    saveButton: {
        width: "100%",
        maxWidth: 300,
        paddingVertical: 12,
        borderRadius: 25,
        alignItems: "center",
        backgroundColor: "#4c956c",
        marginTop: 8,
        marginBottom: 4,
    },
    saveButtonText: {
        color: "#fff",
        fontSize: 16,
        fontWeight: "600",
    },
});

export default GradFundGoalEditor;
